import React from "react"

import {
  Calendar,
  FileSearch,
  FileText,
  Key,
  LogOut,
  Shield,
  ShieldCheck,
  Upload,
  User,
  Users,
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuth } from "@/contexts/AuthContext"
import { supabase } from "@/lib/supabase"

const UserDropdown: React.FC = () => {
  const navigate = useNavigate()
  const { user, session, claims } = useAuth()
  const email = user?.email ?? session?.user?.email ?? "Account"

  const meta = (session?.user?.user_metadata as Partial<{ full_name: string; name: string }>) || {}
  const fullName = user?.full_name ?? meta.full_name ?? meta.name ?? ""
  const firstName =
    (fullName?.trim()?.split(/\s+/)[0] as string | undefined) ||
    (email.includes("@") ? email.split("@")[0] : email)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 text-white hover:bg-white/10 px-3 py-1.5 rounded-md">
          <User className="h-5 w-5" />
          <span className="hidden sm:block max-w-[200px] truncate">{firstName}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Signed in as
          <div className="mt-0.5 text-foreground text-sm font-normal truncate">{email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {claims?.user_role === "Admin" && (
          <>
            {/* Quick link */}
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => navigate("/user-management")}
            >
              <Users className="mr-2 h-4 w-4" />
              <span>Users</span>
            </DropdownMenuItem>

            {/* Grouped Admin submenu */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="cursor-pointer">
                <Shield className="mr-2 h-4 w-4" />
                <span>Admin</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-72">
                <DropdownMenuLabel>Admin</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Permissions
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate("/dev/role-permissions")}
                  >
                    Role Permissions
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate("/dev/team-permissions")}
                  >
                    Team Permissions
                  </DropdownMenuItem>
                </DropdownMenuGroup>

                <DropdownMenuSeparator />

                <DropdownMenuGroup>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    DSP & Driver Manager Teams
                  </DropdownMenuLabel>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/dev/dsp")}>
                    DSP
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate("/dev/driver-manager-teams")}
                  >
                    Driver Manager Teams
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate("/dev/sticky-locations")}
                  >
                    Sticky Locations
                  </DropdownMenuItem>
                </DropdownMenuGroup>

                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Debug
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate("/dev/application-diff")}
                  >
                    Application Diff
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate("/dev/application-stage-history")}
                  >
                    Stage History
                  </DropdownMenuItem>
                </DropdownMenuGroup>

                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Tools
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate("/admin/dvla-recheck")}
                  >
                    DVLA Recheck
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate("/admin/dvla-consent-audit")}
                  >
                    <FileSearch className="mr-2 h-4 w-4" />
                    <span>DVLA Consent Audit</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate("/admin/import-diary-entries")}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    <span>Import Diary Entries</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate("/admin/update-contract-start-date")}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    <span>Update Contract Start Date</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate("/admin/update-driver-manager-team")}
                  >
                    <Users className="mr-2 h-4 w-4" />
                    <span>Update Driver Manager Team</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate("/admin/session-invalidations")}
                  >
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    <span>Session Invalidations</span>
                  </DropdownMenuItem>
                </DropdownMenuGroup>

                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Monitoring
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate("/admin/people-activity-logs")}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    <span>Activity Logs</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate("/admin/audit-logs")}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    <span>Audit Logs</span>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            {/* Top-level Tools item below Admin */}
            <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/dev/api-keys")}>
              <Key className="mr-2 h-4 w-4" />
              <span>API Keys</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem
          className="cursor-pointer text-red-600"
          onClick={async () => {
            try {
              await supabase.auth.signOut()
              toast.success("Logged out", {
                description: "You have been successfully logged out",
              })
              navigate("/login")
            } catch (error) {
              console.error("Logout error:", error)
              toast.error("Logout failed", {
                description: "An error occurred while logging out",
              })
            }
          }}
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default UserDropdown
